<template>
    <CompassTooltip
        :disabled="getUnavailableReasonToRevertImport(data) === null"
        contentClass="tooltip-import-popup"
        :content="labelTooltip"
    >
        <div
            class="card-type-import__wrapper"
            :class="{ 'upsell-action': !isSubscriptionFeatureEnabled }"
        >
            <CompassFeatureBadge
                v-if="!isSubscriptionFeatureEnabled && !loadingMetadata"
                size="small"
                variant="upsell"
            />
            <div
                class="card-type-import"
                :class="customClass"
                :data-bot="data.dataBot"
                @click="onClickCard"
            >
                <CompassSkeleton
                    v-if="loadingMetadata"
                    variant="block"
                    class="card-type-import-skeleton"
                />
                <div v-else class="card-type-import__container">
                    <div class="flex-row jc-space">
                        <div v-if="isSubImport" class="card-type-import__icon">
                            <CompassIcon size="small" :icon="data.icon" />
                            <span class="typographies-paragraph-small-bold main-import-title">
                                {{ data.mainImportTitle }}
                            </span>
                        </div>

                        <CompassObject v-else size="small" :object="data.object" />

                        <input type="radio" :checked="isSelected" />
                        <span />
                    </div>
                    <h4
                        v-if="data.title"
                        class="card-type-import__title compass-heading compass-heading--5"
                    >
                        {{ data.title }}
                    </h4>
                    <p>{{ data.description }}</p>
                </div>
            </div>
        </div>
    </CompassTooltip>
</template>

<script>
import { createNamespacedHelpers } from 'vuex';
const importsStore = createNamespacedHelpers('importsStore');
import importsMixins from 'vcomponents/settings/import/importsMixins.js';

const subscriptionFeaturesStore = createNamespacedHelpers('subscriptionFeatures');
const popupPanelMapActions = createNamespacedHelpers('popupPanel').mapActions;

export default {
    mixins: [importsMixins],
    props: {
        data: {
            type: Object,
            required: true,
        },
        isSubImport: {
            type: Boolean,
            default: false,
        },
    },
    computed: {
        ...importsStore.mapGetters({
            typeImport: 'getTypeImport',
            loadingMetadata: 'getLoadingMetadata',
        }),
        ...subscriptionFeaturesStore.mapGetters({
            subscriptionFeatures: 'getSubscriptionFeatures',
        }),
        isSelected() {
            return this.data.type === this.typeImport?.type;
        },
        customClass() {
            return [
                this.isImportDisabled ? 'disabled' : 'cpointer',
                this.isSelected ? 'selected' : '',
                !this.isSubscriptionFeatureEnabled || this.loadingMetadata ? 'disabled' : '',
            ];
        },
        isImportDisabled() {
            return this.getUnavailableReasonToRevertImport(this.data) !== null;
        },
        labelTooltip() {
            const unavailableReasonToRevertImport = this.getUnavailableReasonToRevertImport(
                this.data,
            );
            if (unavailableReasonToRevertImport !== null) {
                return unavailableReasonToRevertImport === 'blocked_by'
                    ? this.$l10n.__(
                        'Cette action est temporairement indisponible\n pour éviter des erreurs sur vos données\n car un import du même type est déjà en\n cours de traitement',
                    )
                    : this.$l10n.__(
                        'Vous n’avez pas les droits nécessaires pour effectuer cette action',
                    );
            }
            return '';
        },
        isSubscriptionFeatureEnabled() {
            if (this.data.type === 'product-variation') {
                return false !== this.subscriptionFeatures.product_variations;
            }
            return true;
        },
    },
    methods: {
        ...importsStore.mapActions(['updateTypeImport']),
        ...popupPanelMapActions(['open']),
        onClickCard() {
            if (!this.loadingMetadata) {
                if (!this.isSubscriptionFeatureEnabled) {
                    this.open({
                        key: 'featureModal',
                        props: {},
                    });
                } else {
                    if (!this.isImportDisabled) this.updateTypeImport(this.data);
                }
            }
        },
    },
};
</script>

<style lang="scss" scoped>
@use '~@sellsy/styleguide/src/_variables' as *;

.card-type-import {
    background-color: $gray-0;
    border-radius: var(--spacing-0);
    padding: var(--spacing-3);
    transition: background-color 100ms cubic-bezier(0, 0, 0.3, 1);
    height: 100%;

    &__container {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-1);
    }
    &-skeleton {
        height: 84px;
    }
    &__icon {
        display: flex;
        align-items: center;
        gap: var(--spacing-0);
    }

    .main-import-title {
        color: var(--colors-common-text-standard-caption);
    }
    &__title {
        line-height: 20px;
        margin: 0;
    }

    p {
        line-height: 16px;
        margin: 0;
        overflow: hidden;
        display: -webkit-box;
        -webkit-line-clamp: 4;
        -webkit-box-orient: vertical;
    }

    input[type='radio'] {
        display: none;

        &:checked + span {
            color: $primary-50;
        }

        &:checked + span:before {
            background: $primary-50;
            border: $white solid 3px;
            box-shadow: 0 0 0 1px $primary-50;
        }
    }

    input[type='radio'] + span:before {
        background-color: $white;
        border: $white solid 3px;
        box-shadow: 0 0 0 1px $gray-40;
        border-radius: 50%;
        box-sizing: border-box;
        content: '';
        display: inline-block;
        height: 14px;
        transition: all 100ms cubic-bezier(0, 0, 0.3, 1);
        vertical-align: bottom;
        width: 14px;
    }

    &.disabled {
        opacity: 0.5;

        input[type='radio'] + span:before {
            background-color: $gray-05;
            border-color: $gray-05;
        }
    }
    &.selected:hover {
        input[type='radio'] + span {
            &:before {
                border-color: $primary-05;
            }
        }
    }
    &:hover:not(.disabled, .selected) {
        background-color: $primary-0;

        input[type='radio'] + span {
            &:before {
                box-shadow: 0 0 0 1px $primary-50;
                background-color: $primary-05;
                border-color: $primary-05;
            }
        }
    }

    &.selected {
        background-color: var(--colors-component-background-bloc-standard-hover);
        border: 1px solid var(--colors-component-border-actionable-active-default);
        border-radius: 4px;
        box-sizing: border-box;
    }

    &__wrapper {
        position: relative;
        height: 100%;

        &.upsell-action {
            .compass-feature-badge {
                position: absolute;
                right: 0;
                top: 0;
                transform: translate3d(4px, -4px, 0);
                z-index: 1;
            }
        }
    }
}
</style>
